import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { listSolicitacoes, aprovarVinculo, rejeitarVinculo } from '../../services/imovelCorretoresService';

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'pendente', label: 'Pendentes' },
  { value: 'aprovado', label: 'Aprovados' },
  { value: 'rejeitado', label: 'Rejeitados' },
];

const Header = styled.div`
  display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between;
  gap: 16px; margin-bottom: 20px;
  h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 32px; font-weight: 500; margin: 0; color: ${p => p.theme.ink}; }
  p { font-size: 13px; color: ${p => p.theme.muted}; margin: 4px 0 0; }
`;

const Filters = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 12px 16px; margin-bottom: 20px;
  background: ${p => p.theme.surface}; border: 1px solid ${p => p.theme.line}; border-radius: 10px;
`;

const StatusSelect = styled.select`
  padding: 9px 28px 9px 12px;
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  color: ${p => p.theme.ink};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238A8F9A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  transition: border-color .15s;
  &:focus { border-color: ${p => p.theme.gold}; }
`;

const CountLabel = styled.span`
  font-size: 13px; color: ${p => p.theme.muted}; margin-left: auto; white-space: nowrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 180px;
  padding: 9px 12px 9px 34px;
  background: ${p => p.theme.surface} url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='7' stroke='%238A8F9A' stroke-width='2'/%3E%3Cpath d='M16 16l4.5 4.5' stroke='%238A8F9A' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat 10px center;
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  color: ${p => p.theme.ink};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color .15s;
  &:focus { border-color: ${p => p.theme.gold}; }
  &::placeholder { color: ${p => p.theme.muted}; }
`;

const Table = styled.div`
  background: ${p => p.theme.surface}; border: 1px solid ${p => p.theme.line};
  border-radius: 12px; overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 1fr auto;
  align-items: center; gap: 16px;
  padding: 14px 18px; border-top: 1px solid ${p => p.theme.line};
  &:first-of-type { border-top: none; }
  @media (max-width: 760px) { grid-template-columns: 1fr; gap: 8px; }
`;

const Thumb = styled.div`
  width: 56px; height: 42px; border-radius: 6px;
  background: ${p => p.$src ? `url(${p.$src}) center/cover` : p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
`;

const Cell = styled.div`
  font-size: 13px; color: ${p => p.theme.ink};
  .sub { font-size: 11px; color: ${p => p.theme.muted}; margin-top: 2px; }
`;

const Badge = styled.span`
  display: inline-block; padding: 4px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 600; letter-spacing: .4px; text-transform: uppercase;
  background: ${p =>
    p.$s === 'aprovado' ? p.theme.okSoftBg :
    p.$s === 'pendente' ? p.theme.goldSoft :
    'rgba(154,59,59,.1)'};
  color: ${p =>
    p.$s === 'aprovado' ? p.theme.ok :
    p.$s === 'pendente' ? p.theme.gold :
    '#9A3B3B'};
`;

const Actions = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: flex-end;
`;

const Btn = styled.button`
  padding: 6px 12px; border-radius: 6px; font-size: 11px; font-weight: 600;
  letter-spacing: .8px; text-transform: uppercase; cursor: pointer; font-family: inherit;
  border: 1px solid;
  background: ${p => p.$v === 'ok' ? p.theme.ok : p.$v === 'no' ? '#9A3B3B' : 'transparent'};
  color: ${p => p.$v ? '#fff' : p.theme.ink};
  border-color: ${p => p.$v === 'ok' ? p.theme.ok : p.$v === 'no' ? '#9A3B3B' : p.theme.line};
  &:hover:not(:disabled) { opacity: .85; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const Empty = styled.div`
  padding: 80px 20px; text-align: center; color: ${p => p.theme.muted}; font-size: 14px;
`;

const isVideo = (u) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(u || '');
const getCover = (imgs) => {
  if (!Array.isArray(imgs) || !imgs.length) return null;
  return imgs.find(u => !isVideo(u)) || imgs[0];
};

export default function VinculosPanelV2({ theme, admin }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await listSolicitacoes(filter || undefined);
    setItems(data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleAprovar = async (id) => {
    if (!window.confirm('Aprovar este vínculo?')) return;
    setBusy(id);
    try {
      await aprovarVinculo(id, admin?.email || 'admin');
      load();
    } catch (err) {
      alert('Erro ao aprovar: ' + (err.message || ''));
    } finally { setBusy(null); }
  };

  const handleRejeitar = async (id) => {
    const motivo = window.prompt('Motivo da rejeição (opcional):');
    if (motivo === null) return;
    setBusy(id);
    try {
      await rejeitarVinculo(id, motivo || '', admin?.email || 'admin');
      load();
    } catch (err) {
      alert('Erro ao rejeitar: ' + (err.message || ''));
    } finally { setBusy(null); }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.imovel?.titulo || '').toLowerCase().includes(q) ||
      (i.corretor?.nome || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const pendentes = items.filter(i => i.status === 'pendente').length;

  return (
    <>
      <Header theme={theme}>
        <div>
          <h1>Vínculos de imóveis</h1>
          <p>Solicitações de corretores para acessar imóveis da imobiliária. {pendentes > 0 && `${pendentes} pendente${pendentes > 1 ? 's' : ''}.`}</p>
        </div>
      </Header>

      <Filters theme={theme}>
        <SearchInput
          theme={theme}
          type="text"
          placeholder="Buscar imóvel ou corretor..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <StatusSelect theme={theme} value={filter} onChange={e => setFilter(e.target.value)}>
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </StatusSelect>
        <CountLabel theme={theme}>{filtered.length} {filtered.length === 1 ? 'solicitação' : 'solicitações'}</CountLabel>
      </Filters>

      {loading ? (
        <Empty theme={theme}>Carregando...</Empty>
      ) : filtered.length === 0 ? (
        <Empty theme={theme}>Nenhuma solicitação {filter ? STATUS_OPTS.find(o => o.value === filter)?.label.toLowerCase() : ''}{search ? ` para "${search}"` : ''}.</Empty>
      ) : (
        <Table theme={theme}>
          {filtered.map(item => {
            const cover = item.imovel ? getCover(item.imovel.imagens) : null;
            return (
              <Row key={item.id} theme={theme}>
                <Thumb theme={theme} $src={cover} />
                <Cell theme={theme}>
                  <strong>{item.imovel?.titulo || 'Imóvel removido'}</strong>
                  <div className="sub">{[item.imovel?.bairro, item.imovel?.cidade].filter(Boolean).join(', ')}</div>
                </Cell>
                <Cell theme={theme}>
                  {item.corretor?.nome || 'Corretor removido'}
                  <div className="sub">CRECI {item.corretor?.creci}{item.corretor?.creci_uf ? `-${item.corretor.creci_uf}` : ''}</div>
                </Cell>
                <Actions>
                  <Badge theme={theme} $s={item.status}>
                    {item.status === 'aprovado' ? 'Aprovado' : item.status === 'rejeitado' ? 'Rejeitado' : 'Pendente'}
                  </Badge>
                  {item.status === 'pendente' && (
                    <>
                      <Btn theme={theme} $v="ok" disabled={busy === item.id} onClick={() => handleAprovar(item.id)}>
                        Aprovar
                      </Btn>
                      <Btn theme={theme} $v="no" disabled={busy === item.id} onClick={() => handleRejeitar(item.id)}>
                        Rejeitar
                      </Btn>
                    </>
                  )}
                  {item.status === 'aprovado' && (
                    <Btn theme={theme} $v="no" disabled={busy === item.id} onClick={() => handleRejeitar(item.id)}>
                      Revogar
                    </Btn>
                  )}
                </Actions>
              </Row>
            );
          })}
        </Table>
      )}
    </>
  );
}
